
import React from 'react';
// Fix: Use namespace import for react-router-dom to avoid missing exported member errors
import * as ReactRouterDOM from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../contexts/CartContext';
import { Role } from '../../types';

const { NavLink, useLocation } = ReactRouterDOM as any;

// Client Icons
const HomeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>;
const HeartIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z" /></svg>;
const GridIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>;
const CartIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;

// Admin Icons
const DashboardIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>;
const DepositIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;
const OrderIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>;
const UsersIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21v-1a6 6 0 00-1-3.749" /></svg>;

const BottomNavbar: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { cartItems } = useCart();
    const location = useLocation();
    const cartPath = '/cart'; 

    const baseClass = "relative flex flex-col items-center justify-center text-xs gap-1 w-full h-full transition-colors";
    const activeClass = "text-primary-600 dark:text-primary-400 font-medium";
    const inactiveClass = "text-gray-500 dark:text-gray-400 hover:text-primary-500";

    const navLinkClasses = ({isActive}: {isActive: boolean}) => 
        `${baseClass} ${isActive ? activeClass : inactiveClass}`;

    // Menu pour l'Administrateur
    if (user?.role === Role.ADMIN) {
        return (
            <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-t-lg z-40">
                <div className="flex justify-around h-16">
                    <NavLink to="/admin" end className={navLinkClasses}>
                        <DashboardIcon />
                        <span>{t('admin.nav.dashboard')}</span>
                    </NavLink>
                    <NavLink to="/admin/deposits" className={navLinkClasses}>
                        <DepositIcon />
                        <span>{t('admin.nav.deposits')}</span>
                    </NavLink>
                    <NavLink to="/admin/orders" className={navLinkClasses}>
                        <OrderIcon />
                        <span>{t('admin.nav.orders')}</span>
                    </NavLink>
                    <NavLink to="/admin/users" className={navLinkClasses}>
                        <UsersIcon />
                        <span>{t('admin.nav.users')}</span>
                    </NavLink>
                </div>
            </div>
        );
    }

    // Menu pour le Client / Public
    return (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-t-lg z-40">
            <div className="flex justify-around h-16">
                <NavLink to="/" end className={({isActive}:any) => `${baseClass} ${isActive ? activeClass : inactiveClass}`}>
                    <HomeIcon />
                    <span>{t('nav.home')}</span>
                </NavLink>
                
                <NavLink 
                    to="/services?view=favoris" 
                    className={`${baseClass} ${location.pathname === '/services' && location.search.includes('view=favoris') ? activeClass : inactiveClass}`}
                >
                    <HeartIcon />
                    <span>{t('nav.favorites')}</span>
                </NavLink>

                <NavLink 
                    to="/services" 
                    end 
                    className={`${baseClass} ${location.pathname === '/services' && !location.search.includes('view=favoris') ? activeClass : inactiveClass}`}
                >
                    <GridIcon />
                    <span>{t('nav.services')}</span>
                </NavLink>

                <NavLink to={cartPath} className={({isActive}:any) => `${baseClass} ${isActive ? activeClass : inactiveClass}`}>
                    <div className="relative">
                        <CartIcon />
                        {cartItems.length > 0 && (
                            <span className="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary-500 text-xs font-medium text-white">
                            {cartItems.length}
                            </span>
                        )}
                    </div>
                    <span>{t('nav.cart')}</span>
                </NavLink>
            </div>
        </div>
    );
};

export default BottomNavbar;
